import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Dataset, DatasetColumnMetadata } from "../types/api";

interface DatasetDetailPanelProps {
  dataset: Dataset;
  // Breadcrumb text shown above the title, e.g. the dataset's home project.
  crumb: string;
  // Fully caller-controlled primary action (Download / Request access /
  // whatever the context calls for). Use the .dd-action-btn class to match.
  actionButton: React.ReactNode;
  // Rendered between the header row and the meta strip — e.g. a description,
  // a visibility badge, an "uploaded by X on Y" line. Caller's choice.
  children?: React.ReactNode;
}

/** Share of a column's cells that hold a value, 0–100, or null if unknowable. */
const filledPercent = (
  dataset: Dataset,
  nullCount?: number,
): number | null => {
  if (!dataset.row_count || nullCount === undefined) return null;
  return ((dataset.row_count - nullCount) / dataset.row_count) * 100;
};

const formatPercent = (pct: number | null) =>
  pct === null ? "—" : `${pct % 1 === 0 ? pct : pct.toFixed(1)}%`;

const formatStat = (v: number) => {
  if (!Number.isFinite(v)) return "—";
  if (Number.isInteger(v)) return v.toLocaleString();
  return v.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

const hasStats = (col: DatasetColumnMetadata) => col.stats !== undefined;
const hasCategories = (col: DatasetColumnMetadata) =>
  !!col.categories && col.categories.length > 0;
const hasDetail = (col: DatasetColumnMetadata) =>
  hasStats(col) ||
  hasCategories(col) ||
  col.metadata?.unique_count !== undefined;

const FingerprintIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={{ flexShrink: 0 }}
  >
    <path d="M2 12C2 6.5 6.5 2 12 2a10 10 0 0 1 8 4" />
    <path d="M5 19.5C5.5 18 6 15 6 12a6 6 0 0 1 .34-2" />
    <path d="M17.29 21.02c.12-.6.43-2.3.5-3.02" />
    <path d="M12 10a2 2 0 0 0-2 2c0 1.02-.1 2.51-.26 4" />
    <path d="M8.65 22c.21-.66.45-1.32.57-2" />
    <path d="M14 13.12c0 2.38 0 6.38-1 8.88" />
    <path d="M2 16h.01" />
    <path d="M21.8 16c.2-2 .131-5.354 0-6" />
    <path d="M9 6.8a6 6 0 0 1 9 5.2c0 .47 0 1.17-.02 2" />
  </svg>
);

const CaretIcon: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={{
      flexShrink: 0,
      transform: open ? "rotate(90deg)" : "none",
      transition: "transform 0.15s",
    }}
  >
    <path d="m9 18 6-6-6-6" />
  </svg>
);

/**
 * The canonical dataset detail view — metadata strip, column table with
 * type/sensitivity/completeness and the Pseudonymized badge treatment, plus
 * expandable per-column summary statistics.
 *
 * Used by every page that drills into a single dataset (the project-scoped
 * dataset split view, and the global Browse Datasets discovery page) so the
 * two don't drift into separate implementations of the same thing.
 */
export const DatasetDetailPanel: React.FC<DatasetDetailPanelProps> = ({
  dataset,
  crumb,
  actionButton,
  children,
}) => {
  const { t } = useTranslation("datasetDetailPanel");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (name: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  const columns = dataset.column_metadata ?? [];
  const pseudonymizedCount = columns.filter((c) => c.is_pii).length;

  return (
    <>
      <style>{`
        .dd-crumb {
          font-size: 12px;
          color: var(--text-muted);
          margin: 0 0 12px;
        }
        .dd-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 6px;
          gap: 16px;
        }
        .dd-title {
          margin: 0;
          font-size: 22px;
          color: var(--text-primary);
        }
        .dd-action-btn {
          background: var(--fill-primary);
          color: var(--on-primary);
          border: none;
          padding: 8px 16px;
          border-radius: var(--radius);
          font-size: 14px;
          font-family: inherit;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 6px;
          cursor: pointer;
          flex-shrink: 0;
          white-space: nowrap;
        }
        .dd-action-btn:disabled {
          background: var(--fill-disabled);
          color: var(--text-disabled);
          cursor: not-allowed;
        }
        .dd-meta {
          display: flex;
          gap: 32px;
          padding: 16px 0;
          border-top: 0.5px solid var(--border);
          border-bottom: 0.5px solid var(--border);
          margin-bottom: 24px;
          flex-wrap: wrap;
        }
        .dd-meta-label {
          font-size: 11px;
          color: var(--text-muted);
          margin: 0 0 3px;
        }
        .dd-meta-value {
          font-size: 14px;
          font-weight: 500;
          margin: 0;
          color: var(--text-primary);
        }
        .dd-columns-label {
          font-size: 13px;
          font-weight: 500;
          color: var(--text-secondary);
          margin: 0 0 10px;
        }
        .dd-columns-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13px;
        }
        .dd-columns-table th {
          text-align: left;
          padding: 8px 4px;
          color: var(--text-muted);
          font-weight: 500;
          border-bottom: 0.5px solid var(--border);
        }
        .dd-columns-table th.right {
          text-align: right;
        }
        .dd-columns-table td {
          padding: 8px 4px;
          border-bottom: 0.5px solid var(--border);
        }
        .dd-columns-table tr:last-child td {
          border-bottom: none;
        }
        .dd-col-row.expandable { cursor: pointer; }
        .dd-col-row.expandable:hover { background: var(--surface-1); }
        .dd-caret-cell { width: 20px; color: var(--text-muted); }
        .dd-col-name {
          font-family: var(--font-mono, ui-monospace, monospace);
        }
        .dd-col-type {
          color: var(--text-secondary);
        }
        .dd-col-right {
          text-align: right;
          color: var(--text-secondary);
        }
        .dd-pseudo-badge {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          background: var(--bg-accent-muted);
          color: var(--text-accent);
          font-size: 12px;
          padding: 3px 9px;
          border-radius: var(--radius);
          cursor: help;
        }
        .dd-col-none {
          color: var(--text-muted);
        }
        .dd-pseudo-legend {
          font-size: 12px;
          color: var(--text-muted);
          margin: 10px 0 0;
          display: flex;
          align-items: flex-start;
          gap: 5px;
          line-height: 1.5;
        }
        .dd-stats-cell {
          background: var(--surface-1);
          padding: 12px 14px 14px 24px !important;
        }
        .dd-stats-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 20px;
        }
        .dd-stat-label {
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: var(--text-muted);
          margin: 0 0 2px;
        }
        .dd-stat-value {
          font-size: 13px;
          font-weight: 600;
          color: var(--text-primary);
          margin: 0;
          font-variant-numeric: tabular-nums;
        }
        .dd-cat-title {
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: var(--text-muted);
          margin: 0 0 6px;
        }
        .dd-cat-chips { display: flex; flex-wrap: wrap; gap: 5px; }
        .dd-cat-chip {
          font-size: 12px;
          background: var(--surface-2);
          border: 0.5px solid var(--border);
          border-radius: 5px;
          padding: 2px 7px;
          color: var(--text-primary);
        }
        .dd-cat-count { color: var(--text-muted); margin-left: 4px; }
        .dd-cat-more { font-size: 12px; color: var(--text-muted); padding: 2px 4px; }
        .dd-stats-note { font-size: 12px; color: var(--text-muted); margin: 10px 0 0; }
      `}</style>

      <p className="dd-crumb">
        {crumb} &rsaquo; {dataset.display_name}
      </p>
      <div className="dd-header">
        <h1 className="dd-title">{dataset.display_name}</h1>
        {actionButton}
      </div>

      {children}

      <div className="dd-meta">
        <div>
          <p className="dd-meta-label">{t("meta.rows")}</p>
          <p className="dd-meta-value">
            {dataset.row_count > 0 ? dataset.row_count.toLocaleString() : "—"}
          </p>
        </div>
        <div>
          <p className="dd-meta-label">{t("meta.columns")}</p>
          <p className="dd-meta-value">
            {columns.length > 0 ? columns.length : "—"}
          </p>
        </div>
        <div>
          <p className="dd-meta-label">{t("meta.pseudonymized")}</p>
          <p className="dd-meta-value">
            {columns.length > 0
              ? t("meta.pseudonymizedRatio", {
                  count: pseudonymizedCount,
                  total: columns.length,
                })
              : "—"}
          </p>
        </div>
        <div>
          <p className="dd-meta-label">{t("meta.created")}</p>
          <p className="dd-meta-value">{formatDate(dataset.created_at)}</p>
        </div>
        <div>
          <p className="dd-meta-label">{t("meta.expires")}</p>
          <p className="dd-meta-value">
            {dataset.expires_at ? formatDate(dataset.expires_at) : t("meta.noExpiry")}
          </p>
        </div>
      </div>

      {columns.length > 0 && (
        <>
          <p className="dd-columns-label">
            {t("columnsSectionLabel")}
            {columns.some(hasDetail) && (
              <span style={{ fontWeight: 400, color: "var(--text-muted)" }}>
                {" "}
                {t("selectRowHint")}
              </span>
            )}
          </p>
          <table className="dd-columns-table">
            <thead>
              <tr>
                <th className="dd-caret-cell" />
                <th>{t("table.column")}</th>
                <th>{t("table.type")}</th>
                <th>{t("table.sensitivity")}</th>
                <th className="right">{t("table.complete")}</th>
              </tr>
            </thead>
            <tbody>
              {columns.map((col) => {
                const pct = filledPercent(dataset, col.null_count);
                const detail = hasDetail(col);
                const open = expanded.has(col.name);
                return (
                  <React.Fragment key={col.name}>
                    <tr
                      className={`dd-col-row ${detail ? "expandable" : ""}`}
                      onClick={detail ? () => toggle(col.name) : undefined}
                    >
                      <td className="dd-caret-cell">
                        {detail && <CaretIcon open={open} />}
                      </td>
                      <td className="dd-col-name">{col.name}</td>
                      <td className="dd-col-type">{col.type}</td>
                      <td>
                        {col.is_pii ? (
                          <span
                            className="dd-pseudo-badge"
                            title={t("pseudonymizedBadgeTitle", { column: col.name })}
                          >
                            <FingerprintIcon size={14} />
                            {t("pseudonymizedBadge")}
                          </span>
                        ) : (
                          <span className="dd-col-none">—</span>
                        )}
                      </td>
                      <td className="dd-col-right">{formatPercent(pct)}</td>
                    </tr>

                    {open && detail && (
                      <tr>
                        <td className="dd-stats-cell" colSpan={5}>
                          {hasStats(col) && (
                            <div className="dd-stats-grid">
                              <div>
                                <p className="dd-stat-label">{t("stats.min")}</p>
                                <p className="dd-stat-value">
                                  {formatStat(col.stats!.min)}
                                </p>
                              </div>
                              <div>
                                <p className="dd-stat-label">{t("stats.max")}</p>
                                <p className="dd-stat-value">
                                  {formatStat(col.stats!.max)}
                                </p>
                              </div>
                              <div>
                                <p className="dd-stat-label">{t("stats.mean")}</p>
                                <p className="dd-stat-value">
                                  {formatStat(col.stats!.mean)}
                                </p>
                              </div>
                              <div>
                                <p className="dd-stat-label">{t("stats.median")}</p>
                                <p className="dd-stat-value">
                                  {formatStat(col.stats!.median)}
                                </p>
                              </div>
                              <div>
                                <p className="dd-stat-label">{t("stats.stdDev")}</p>
                                <p className="dd-stat-value">
                                  {formatStat(col.stats!.std_dev)}
                                </p>
                              </div>
                            </div>
                          )}

                          {hasCategories(col) && (
                            <div style={{ marginTop: hasStats(col) ? 14 : 0 }}>
                              <p className="dd-cat-title">
                                {t("categories", { count: col.categories!.length })}
                              </p>
                              <div className="dd-cat-chips">
                                {col.categories!.slice(0, 20).map((c) => (
                                  <span className="dd-cat-chip" key={c}>
                                    {c}
                                    {col.category_counts?.[c] !== undefined && (
                                      <span className="dd-cat-count">
                                        {col.category_counts[c].toLocaleString()}
                                      </span>
                                    )}
                                  </span>
                                ))}
                                {col.categories!.length > 20 && (
                                  <span className="dd-cat-more">
                                    {t("moreCategories", { count: col.categories!.length - 20 })}
                                  </span>
                                )}
                              </div>
                            </div>
                          )}

                          {col.metadata?.unique_count !== undefined && (
                            <p className="dd-stats-note">
                              {t("uniqueValues", {
                                count: col.metadata.unique_count,
                                formatted: col.metadata.unique_count.toLocaleString(),
                              })}
                              {col.is_pii && ` ${t("uniqueNoExposure")}`}
                              {col.null_count !== undefined &&
                                ` ${t("emptyCount", { formatted: col.null_count.toLocaleString() })}`}
                            </p>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          {pseudonymizedCount > 0 && (
            <p className="dd-pseudo-legend">
              <FingerprintIcon size={13} />
              <span>{t("legend")}</span>
            </p>
          )}
        </>
      )}
    </>
  );
};
